<script lang="ts" setup>
import { type ApiTokenCreateRequest, type ApiTokenCreateResponse } from '@/types'
import { STATUS_CODES } from '@/constants'
import { reactive, watch, ref } from 'vue'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  show: boolean
}>()

const emits = defineEmits<{
  (e: 'close'): void
  (e: 'created', response: ApiTokenCreateResponse): void
}>()

const notifyStore = useNotifyStore()
const { t } = useI18n()

const data = reactive({
  showDialog: false,
  loading: false,
  name: '',
  createdToken: null as string | null,
  createdTokenInfo: null as ApiTokenCreateResponse['info'] | null
})

const tokenCopied = ref(false)

const handleClose = () => {
  data.showDialog = false
}

const resetForm = () => {
  data.name = ''
  data.createdToken = null
  data.createdTokenInfo = null
  tokenCopied.value = false
}

const handleCreate = async () => {
  if (!data.name.trim()) return

  data.loading = true

  const requestData: ApiTokenCreateRequest = {
    name: data.name.trim()
  }

  try {
    const res = await useAxios<ApiTokenCreateRequest, ApiTokenCreateResponse>(
      'POST',
      '/account/tokens',
      true,
      requestData
    )

    if (res && res.status === STATUS_CODES.CREATED && res.data) {
      data.createdToken = res.data.token
      data.createdTokenInfo = res.data.info
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'ApiTokenCreateModal' })

    const errorMessage = getErrorMessage(error as Error)
    if (errorMessage.includes('maximum') || errorMessage.includes('limit')) {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'warning',
        title: t('dashboard.account.apiTokens.notifications.maxTokensReached'),
        message: ''
      })
    } else {
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('common.error'),
        message: errorMessage
      })
    }
  } finally {
    data.loading = false
  }
}

const copyToken = async () => {
  if (!data.createdToken) return

  try {
    await navigator.clipboard.writeText(data.createdToken)
    tokenCopied.value = true
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'success',
      title: t('dashboard.account.apiTokens.copied'),
      message: ''
    })
    setTimeout(() => {
      tokenCopied.value = false
    }, 2000)
  } catch {
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: 'Clipboard not available'
    })
  }
}

watch(
  () => props.show,
  (newVal) => {
    data.showDialog = newVal
    if (newVal) {
      resetForm()
    }
  },
  { immediate: true }
)

watch(
  () => data.showDialog,
  (newVal) => {
    if (!newVal) {
      if (data.createdToken && data.createdTokenInfo) {
        emits('created', { token: data.createdToken, info: data.createdTokenInfo })
      }
      emits('close')
    }
  }
)
</script>

<template>
  <v-dialog v-model="data.showDialog" max-width="500" persistent>
    <div class="create-modal" :class="{ 'success-state': data.createdToken }">
      <!-- Header -->
      <div class="modal-header">
        <div class="header-icon" :class="{ success: data.createdToken }">
          <v-icon :icon="data.createdToken ? 'mdi-check-circle' : 'mdi-key-plus'" size="24" />
        </div>
        <div class="header-text">
          <h2 class="modal-title">
            {{ data.createdToken ? $t('dashboard.account.apiTokens.create.successTitle') : $t('dashboard.account.apiTokens.create.title') }}
          </h2>
          <p v-if="data.createdTokenInfo" class="modal-subtitle">{{ data.createdTokenInfo.name }}</p>
        </div>
        <button class="close-btn" @click="handleClose">
          <v-icon icon="mdi-close" size="22" />
        </button>
      </div>

      <!-- Create Form -->
      <div v-if="!data.createdToken" class="modal-content">
        <div class="form-group">
          <label class="form-label">{{ $t('dashboard.account.apiTokens.create.name') }}</label>
          <input
            v-model="data.name"
            type="text"
            class="form-input"
            :placeholder="$t('dashboard.account.apiTokens.create.namePlaceholder')"
            @keyup.enter="handleCreate"
          />
          <span class="form-hint">
            <v-icon icon="mdi-information-outline" size="14" />
            {{ $t('dashboard.account.apiTokens.create.nameHint') }}
          </span>
        </div>
      </div>

      <!-- Success State - Token Display -->
      <div v-else class="modal-content">
        <!-- Warning -->
        <div class="warning-box">
          <v-icon icon="mdi-alert" size="20" />
          <span>{{ $t('dashboard.account.apiTokens.create.warning') }}</span>
        </div>

        <!-- Token Display -->
        <div class="token-display">
          <label class="token-label">Token</label>
          <div class="token-value-wrapper">
            <code class="token-value">{{ data.createdToken }}</code>
            <button class="copy-token-btn" :class="{ copied: tokenCopied }" @click="copyToken">
              <v-icon :icon="tokenCopied ? 'mdi-check' : 'mdi-content-copy'" size="18" />
              {{ tokenCopied ? $t('dashboard.account.apiTokens.copied') : $t('dashboard.account.apiTokens.copy') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="modal-footer">
        <template v-if="!data.createdToken">
          <button class="btn secondary" :disabled="data.loading" @click="handleClose">
            {{ $t('common.cancel') }}
          </button>
          <button class="btn primary" :disabled="data.loading || !data.name.trim()" @click="handleCreate">
            <v-progress-circular v-if="data.loading" indeterminate size="18" width="2" />
            <v-icon v-else icon="mdi-plus" size="18" />
            {{ $t('dashboard.account.apiTokens.createToken') }}
          </button>
        </template>
        <template v-else>
          <button class="btn primary" @click="handleClose">
            <v-icon icon="mdi-check" size="18" />
            {{ $t('common.done') }}
          </button>
        </template>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.create-modal {
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);

  &.success-state {
    border-color: rgba(76, 175, 80, 0.2);
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(33, 150, 243, 0.12);
  border: 1px solid rgba(33, 150, 243, 0.25);
  border-radius: 12px;
  color: #64b5f6;

  &.success {
    background: rgba(76, 175, 80, 0.12);
    border-color: rgba(76, 175, 80, 0.25);
    color: #81c784;
  }
}

.header-text {
  flex: 1;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 2px;
}

.modal-subtitle {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
  margin: 0;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.5);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    color: rgba(200, 218, 255, 0.9);
  }
}

.modal-content {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.form-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.8);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 14px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 10px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(200, 218, 255, 0.4);
  }

  &:focus {
    border-color: rgba(33, 150, 243, 0.4);
  }
}

.form-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);

  .v-icon {
    color: rgba(200, 218, 255, 0.4);
  }
}

.warning-box {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.25);
  border-radius: 12px;
  margin-bottom: 20px;

  .v-icon {
    color: #ffb74d;
    flex-shrink: 0;
    margin-top: 1px;
  }

  span {
    font-size: 0.9rem;
    color: #ffb74d;
    line-height: 1.5;
  }
}

.token-display {
  margin-bottom: 0;
}

.token-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(200, 218, 255, 0.5);
  margin-bottom: 8px;
}

.token-value-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(76, 175, 80, 0.08);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 10px;
}

.token-value {
  flex: 1;
  font-size: 0.95rem;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  color: #81c784;
  background: transparent;
  white-space: nowrap;
  overflow-x: auto;
  padding-bottom: 4px;

  &::-webkit-scrollbar {
    height: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(76, 175, 80, 0.3);
    border-radius: 2px;
  }
}

.copy-token-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  color: #81c784;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.45);
  }

  &.copied {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.5);
  }
}

.modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.secondary {
    background: rgba(120, 163, 255, 0.08);
    border: 1px solid rgba(120, 163, 255, 0.15);
    color: rgba(200, 218, 255, 0.8);

    &:hover:not(:disabled) {
      background: rgba(120, 163, 255, 0.15);
    }
  }

  &.primary {
    background: rgba(33, 150, 243, 0.15);
    border: 1px solid rgba(33, 150, 243, 0.3);
    color: #64b5f6;

    &:hover:not(:disabled) {
      background: rgba(33, 150, 243, 0.25);
      border-color: rgba(33, 150, 243, 0.45);
    }
  }
}

.success-state .btn.primary {
  background: rgba(76, 175, 80, 0.15);
  border-color: rgba(76, 175, 80, 0.3);
  color: #81c784;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.45);
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .create-modal {
    background: rgba(255, 255, 255, 0.98);
  }

  .modal-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .modal-subtitle {
    color: rgba(15, 23, 42, 0.6);
  }

  .form-label {
    color: rgba(15, 23, 42, 0.8);
  }

  .form-input {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(15, 23, 42, 0.9);

    &::placeholder {
      color: rgba(15, 23, 42, 0.4);
    }
  }

  .form-hint {
    color: rgba(15, 23, 42, 0.5);
  }
}

/* Responsive */
@media (max-width: 600px) {
  .token-value-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .copy-token-btn {
    justify-content: center;
  }
}
</style>
